// ข้อมูลนิทรรศการถุงช้อปปิ้ง Big C (แยกจาก JSX เพื่อขยายหลายจังหวัด / ต่อ CMS ภายหลัง)
// Texture + artwork-manifest.json สร้างจาก scripts/bigc-exhibition/crop_artwork.py (ตรวจขอบ panel อัตโนมัติรายไฟล์)
// คำบรรยาย Hotspot อธิบายสิ่งที่ปรากฏบน Artwork เท่านั้น — ยังไม่มีสเปกวัสดุ/ขนาดที่ยืนยัน จึงไม่ใส่ specifications
import manifest from "./artwork-manifest.json";
// เนื้อหาสองภาษาของทุกถุง (V2) — { [id]: { teaserTh, teaserEn, storyTh, storyEn, accent,
//   hotspots: [[id, view, u, v, titleTh, descriptionTh, titleEn, descriptionEn, category], ...] } }
// category: landmark (สถานที่สำคัญ) | identity (เอกลักษณ์เมือง) | product (ของดีประจำจังหวัด)
import bagContent from "./bag-content.json";

export const VIEWS = ["front", "side1", "back", "side2", "bottom"];

export const VIEW_LABELS = {
  front: { th: "ด้านหน้า", en: "Front" },
  side1: { th: "ด้านข้างขวา", en: "Right Side" }, // ต่อขอบขวาของด้านหน้า (มองจากด้านหน้า)
  back: { th: "ด้านหลัง", en: "Back" },
  side2: { th: "ด้านข้างซ้าย", en: "Left Side" }, // ต่อขอบซ้ายของด้านหน้า
  bottom: { th: "ด้านล่าง", en: "Bottom" },
};

const BASE = `${process.env.PUBLIC_URL || ""}/bigc-exhibition`;

// ใช้เมื่อ manifest ไม่มีข้อมูลหูหิ้ว (สัดส่วนเทียบความกว้าง/สูงของ panel)
const DEFAULT_HANDLE = { left: 0.29, width: 0.448, thickness: 0.076, height: 0.445, color: "#999999" };

// ขนาดจริงของถุง THAILAND EDITION (จากไฟล์ BIG C SHOPPING BAG 26 Storytelling): W30 x H40 x L18 cm
export const BAG_SIZE_CM = { width: 30, height: 40, depth: 18 };

const hs = (id, view, u, v, titleTh, descriptionTh, titleEn, descriptionEn, category) => ({
  id, view, u, v, titleTh, descriptionTh, titleEn, descriptionEn, category,
});

// ภูมิภาคตามไฟล์ BIG C SHOPPING BAG 26 Storytelling (ใช้กรองใน Gallery และสีบนแผนที่)
export const REGIONS = [
  { id: "central", th: "ภาคกลาง", en: "Central" },
  { id: "north", th: "ภาคเหนือ", en: "North" },
  { id: "northeast", th: "ภาคตะวันออกเฉียงเหนือ", en: "Northeast" },
  { id: "east", th: "ภาคตะวันออก", en: "East" },
  { id: "west", th: "ภาคตะวันตก", en: "West" },
  { id: "south", th: "ภาคใต้", en: "South" },
];

// จับคู่ถุงกับจังหวัดบนแผนที่ (thailand-map.json): key = ชื่ออังกฤษตัวเล็กไม่มีช่องว่าง
const MAP_KEY_ALIASES = { ayutthaya: "phranakhonsiayutthaya" };
const mapKeyOf = (provinceEn) => {
  const k = provinceEn.toLowerCase().replace(/[\s-]/g, "");
  return MAP_KEY_ALIASES[k] || k;
};

// ประกอบ record จาก manifest: texture, กรอบตัด, สัดส่วนถุง, หูหิ้วหน้า/หลัง (สีแยกกันได้)
const build = (record) => {
  const m = manifest[record.id] || {};
  const textures = VIEWS.reduce((acc, view) => {
    acc[view] = `${BASE}/bags/${record.id}/${view}.webp`;
    return acc;
  }, {});
  const handles = {
    front: { ...DEFAULT_HANDLE, ...(m.handles?.front || {}) },
    back: { ...DEFAULT_HANDLE, ...(m.handles?.back || m.handles?.front || {}) },
  };
  const story = bagContent[record.id] || {};
  return {
    published: true,
    teaserTh: `ถุงช้อปปิ้ง Big C ลายจังหวัด${record.provinceTh}`,
    teaserEn: `Big C shopping bag inspired by ${record.provinceEn}`,
    storyTh: `ลวดลายรอบถุงทั้ง 5 ด้านถ่ายทอดเอกลักษณ์ของจังหวัด${record.provinceTh} หมุนและซูมเพื่อสำรวจรายละเอียดบนถุงได้ (กำลังเตรียมข้อมูลจุดเด่น)`,
    storyEn: `All five sides of this bag celebrate ${record.provinceEn}. Rotate and zoom to explore the details.`,
    accent: "#df1730",
    hotspots: [],
    ...record,
    ...(story.teaserTh && { teaserTh: story.teaserTh }),
    ...(story.teaserEn && { teaserEn: story.teaserEn }),
    ...(story.storyTh && { storyTh: story.storyTh }),
    ...(story.storyEn && { storyEn: story.storyEn }),
    ...(story.accent && { accent: story.accent }),
    ...(story.hotspots && { hotspots: story.hotspots.map((h) => hs(...h)) }),
    textures,
    thumbnail: `${BASE}/bags/${record.id}/thumb.webp`,
    artworkSource: `${BASE}/source/${record.id}.jpg`,
    cropBoxes: m.cropBoxes,
    // รูปทรงถุง 3D ตามขนาดจริง (ทุกใบเท่ากัน) — texture ถูกยืดให้พอดีด้าน ต่างจากภาพตัดไม่เกิน ~1%
    proportions: { height: BAG_SIZE_CM.height / BAG_SIZE_CM.width, depth: BAG_SIZE_CM.depth / BAG_SIZE_CM.width },
    handles,
    handleColor: handles.front.color,
    mapKey: mapKeyOf(record.provinceEn),
    // 3 สีเด่นของลายถุง (จาก crop_artwork.py --themes) ใช้ทำพื้นหลังหน้าให้ล้อตามธีมถุง
    theme: m.theme || ["#cfe6f7", "#eef6fb", "#f6f1e7"],
  };
};

const collections = [
  {
    id: "prachuap",
    region: "west",
    slug: "prachuap-khiri-khan",
    provinceTh: "ประจวบคีรีขันธ์",
    provinceEn: "Prachuap Khiri Khan",
  },
  {
    id: "nakhon-pathom",
    region: "central",
    slug: "nakhon-pathom",
    provinceTh: "นครปฐม",
    provinceEn: "Nakhon Pathom",
  },
  {
    id: "bangkok-thailand-edition",
    region: "central",
    slug: "bangkok-thailand-edition",
    provinceTh: "กรุงเทพมหานคร",
    provinceEn: "Bangkok",
    editionEn: "Thailand Edition",
  },
  {
    id: "bangkok-one-bangkok",
    region: "central",
    slug: "bangkok-one-bangkok",
    provinceTh: "กรุงเทพมหานคร",
    provinceEn: "Bangkok",
    editionEn: "One Bangkok",
  },
  {
    id: "bangkok-food",
    region: "central",
    slug: "bangkok-food",
    provinceTh: "กรุงเทพมหานคร",
    provinceEn: "Bangkok",
    editionEn: "Food & Drinks",
  },
  {
    id: "bangkok-ratchadamri",
    region: "central",
    slug: "bangkok-ratchadamri",
    provinceTh: "กรุงเทพมหานคร",
    provinceEn: "Bangkok",
    editionEn: "Ratchadamri",
  },
  {
    id: "bangkok-asiatique",
    region: "central",
    slug: "bangkok-asiatique",
    provinceTh: "กรุงเทพมหานคร",
    provinceEn: "Bangkok",
    editionEn: "Asiatique",
  },
  {
    id: "nakhon-sawan",
    region: "central",
    slug: "nakhon-sawan",
    provinceTh: "นครสวรรค์",
    provinceEn: "Nakhon Sawan",
  },
  {
    id: "pathum-thani",
    region: "central",
    slug: "pathum-thani",
    provinceTh: "ปทุมธานี",
    provinceEn: "Pathum Thani",
  },
  {
    id: "ayutthaya",
    region: "central",
    slug: "ayutthaya",
    provinceTh: "พระนครศรีอยุธยา",
    provinceEn: "Ayutthaya",
  },
  {
    id: "ratchaburi",
    region: "west",
    slug: "ratchaburi",
    provinceTh: "ราชบุรี",
    provinceEn: "Ratchaburi",
  },
];

// ถุงจาก bigc-exhibition-source/Raw — [เลขไฟล์, id, ชื่อไทย, ชื่ออังกฤษ, ภาค] (id ตรงกับ RAW_IDS ใน crop_artwork.py)
const RAW_PROVINCES = [
  [7, "lopburi", "ลพบุรี", "Lop Buri", "central"],
  [8, "samut-prakan", "สมุทรปราการ", "Samut Prakan", "central"],
  [9, "samut-songkhram", "สมุทรสงคราม", "Samut Songkhram", "central"],
  [10, "samut-sakhon", "สมุทรสาคร", "Samut Sakhon", "central"],
  [11, "saraburi", "สระบุรี", "Saraburi", "central"],
  [12, "suphan-buri", "สุพรรณบุรี", "Suphan Buri", "central"],
  [13, "sukhothai", "สุโขทัย", "Sukhothai", "north"],
  [14, "ang-thong", "อ่างทอง", "Ang Thong", "central"],
  [15, "chanthaburi", "จันทบุรี", "Chanthaburi", "east"],
  [16, "chachoengsao", "ฉะเชิงเทรา", "Chachoengsao", "east"],
  [17, "chon-buri", "ชลบุรี", "Chon Buri", "east"],
  [18, "prachin-buri", "ปราจีนบุรี", "Prachin Buri", "east"],
  [19, "rayong", "ระยอง", "Rayong", "east"],
  [20, "sa-kaeo", "สระแก้ว", "Sa Kaeo", "east"],
  [21, "kanchanaburi", "กาญจนบุรี", "Kanchanaburi", "west"],
  [22, "tak", "ตาก", "Tak", "west"],
  [24, "phetchaburi", "เพชรบุรี", "Phetchaburi", "west"],
  [25, "kamphaeng-phet", "กำแพงเพชร", "Kamphaeng Phet", "north"],
  [26, "chiang-mai", "เชียงใหม่", "Chiang Mai", "north"],
  [27, "chiang-rai", "เชียงราย", "Chiang Rai", "north"],
  [28, "nan", "น่าน", "Nan", "north"],
  [29, "phetchabun", "เพชรบูรณ์", "Phetchabun", "north"],
  [30, "phrae", "แพร่", "Phrae", "north"],
  [31, "lampang", "ลำปาง", "Lampang", "north"],
  [32, "lamphun", "ลำพูน", "Lamphun", "north"],
  [33, "kalasin", "กาฬสินธุ์", "Kalasin", "northeast"],
  [34, "khon-kaen", "ขอนแก่น", "Khon Kaen", "northeast"],
  [35, "chaiyaphum", "ชัยภูมิ", "Chaiyaphum", "northeast"],
  [36, "nakhon-phanom", "นครพนม", "Nakhon Phanom", "northeast"],
  [37, "nakhon-ratchasima", "นครราชสีมา", "Nakhon Ratchasima", "northeast"],
  [38, "buri-ram", "บุรีรัมย์", "Buri Ram", "northeast"],
  [39, "maha-sarakham", "มหาสารคาม", "Maha Sarakham", "northeast"],
  [40, "mukdahan", "มุกดาหาร", "Mukdahan", "northeast"],
  [41, "yasothon", "ยโสธร", "Yasothon", "northeast"],
  [42, "roi-et", "ร้อยเอ็ด", "Roi Et", "northeast"],
  [43, "loei", "เลย", "Loei", "northeast"],
  [44, "si-sa-ket", "ศรีสะเกษ", "Si Sa Ket", "northeast"],
  [45, "sakon-nakhon", "สกลนคร", "Sakon Nakhon", "northeast"],
  [46, "surin", "สุรินทร์", "Surin", "northeast"],
  [47, "nong-khai", "หนองคาย", "Nong Khai", "northeast"],
  [48, "amnat-charoen", "อำนาจเจริญ", "Amnat Charoen", "northeast"],
  [49, "udon-thani", "อุดรธานี", "Udon Thani", "northeast"],
  [50, "ubon-ratchathani", "อุบลราชธานี", "Ubon Ratchathani", "northeast"],
  [51, "krabi", "กระบี่", "Krabi", "south"],
  [52, "chumphon", "ชุมพร", "Chumphon", "south"],
  [53, "trang", "ตรัง", "Trang", "south"],
  [54, "nakhon-si-thammarat", "นครศรีธรรมราช", "Nakhon Si Thammarat", "south"],
  [55, "narathiwat", "นราธิวาส", "Narathiwat", "south"],
  [56, "pattani", "ปัตตานี", "Pattani", "south"],
  [57, "phang-nga", "พังงา", "Phang Nga", "south"],
  [58, "phuket", "ภูเก็ต", "Phuket", "south"],
  [59, "yala", "ยะลา", "Yala", "south"],
  [60, "ranong", "ระนอง", "Ranong", "south"],
  [61, "songkhla", "สงขลา", "Songkhla", "south"],
  [62, "satun", "สตูล", "Satun", "south"],
  [63, "surat-thani", "สุราษฎร์ธานี", "Surat Thani", "south"],
  [64, "phitsanulok", "พิษณุโลก", "Phitsanulok", "north"],
  [65, "nonthaburi", "นนทบุรี", "Nonthaburi", "central"],
];

collections.push(
  ...RAW_PROVINCES.map(([no, id, provinceTh, provinceEn, region]) => ({ no, id, slug: id, provinceTh, provinceEn, region }))
);

// แสดงเฉพาะใบที่ตัดรูปผ่านแล้ว (มีใน manifest)
const builtCollections = collections.filter((c) => manifest[c.id]).map(build);

export const publishedCollections = builtCollections.filter((c) => c.published);

// สัดส่วนสูง/กว้างของ texture แต่ละด้าน (ใช้จัดตำแหน่งภาพย่อและจุดเด่นในหน้าซูม)
export const faceAspect = (collection, view) => {
  const { height, depth } = collection.proportions;
  if (view === "side1" || view === "side2") return height / depth;
  if (view === "bottom") return depth;
  return height;
};

export const findCollection = (key) =>
  publishedCollections.find((c) => c.slug === key || c.id === key);

// ถุงที่แสดงเมื่อเข้า /BigC_Exhibition โดยไม่ระบุจังหวัด (และเมื่อ slug ไม่ถูกต้อง)
export const DEFAULT_COLLECTION_ID = "bangkok-thailand-edition";
export const defaultCollection = findCollection(DEFAULT_COLLECTION_ID) || publishedCollections[0];
